"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = useCommunityEnabled;
/**
 * Shared gate for **community profile** surfaces (profiles, playlists in profile UX,
 * my library shell, comment profile links, header menu avatars, etc.).
 *
 * Two layers of gating:
 *
 *   1. OpenShift / config flag (`features.communityProfile`)
 *      - Set per environment via FEATURES_COMMUNITY_PROFILE env var
 *      - If OFF (0) the feature is completely disabled, full stop
 *
 *   2. PostHog feature flag (`community-employee-test`)
 *      - Only evaluated when PostHog itself is enabled (`features.posthog`)
 *      - Boolean flag — user must have the flag enabled to see community profile UX
 *      - In local dev / staging where PostHog is OFF, we skip this check
 *        and treat the feature as enabled (so devs can work on it)
 *
 * Returns:
 *   enabled  — true when the current user should see community profile features
 *   loading  — true while PostHog is resolving the flag (brief flash)
 */
var react_1 = require("@posthog/react");
var index_1 = require("../../../components/PostHog/index");
var index_2 = require("../../../config/index");
var environment_1 = require("../../../config/environment");
function useCommunityEnabled() {
    var _a, _b;
    var config = (0, index_2.get)();
    var configEnabled = !!((_a = config === null || config === void 0 ? void 0 : config.features) === null || _a === void 0 ? void 0 : _a.communityProfile);
    var isPostHogEnabled = !!((_b = config === null || config === void 0 ? void 0 : config.features) === null || _b === void 0 ? void 0 : _b.posthog);
    var communityEmployeeFlag = (0, react_1.useFeatureFlagEnabled)(index_1.POSTHOG_FEATURE_FLAGS.COMMUNITY_EMPLOYEE_TEST);
    // Determine final enabled state
    var enabled = false;
    if (configEnabled) {
        if (!isPostHogEnabled) {
            // PostHog is OFF (local dev / staging) — config flag alone is enough
            enabled = true;
        }
        else {
            // PostHog is ON (production) — user must have the boolean flag enabled
            enabled = !!communityEmployeeFlag;
        }
    }
    // With advanced_disable_feature_flags: true, PostHog flags bootstrap
    // synchronously at module load time — no async flag resolution occurs.
    // In the browser, an undefined flag means it wasn't in the bootstrap
    // payload (user not in cohort), not "still loading." Only treat as
    // loading during SSR where PostHog genuinely isn't available yet.
    var loading = !!(configEnabled
        && isPostHogEnabled
        && communityEmployeeFlag === undefined
        && !(0, environment_1.isBrowser)());
    return { enabled: enabled, loading: loading };
}
